// src/components/wallet.jsx
// UPDATED: Esdras Wallet with Marketplace integration
// Changes from original:
//   1. Listens to BOTH wallets/{uid} AND users/{uid} — users doc holds walletBalance
//      used for marketplace payments (merchantWalletPayment fn deducts from users/{uid})
//   2. Marketplace section: shows balance usable on marketplace + CTA link
//   3. Transaction history merged from grooming transactions AND merchant_orders (wallet method)
//   4. Balance shown is the HIGHER of the two sources for clarity, with tooltip
//   5. All original functionality preserved exactly (Monnify fund, bank save, payout request)

import React, { useState, useEffect } from 'react';
import { getAuth } from 'firebase/auth';
import {
  doc, onSnapshot, collection, query,
  orderBy, limit, setDoc, getDocs, where, getDoc,
} from 'firebase/firestore';
import { db } from '../firebase';
import { useNavigate } from 'react-router-dom';
import PayoutRequest from './payoutrequest';
import toast from 'react-hot-toast';
import { useSubscription, formatNaira } from '../hooks/useSubscription';

const NAVY = '#001F3F';
const GOLD = '#B8860B';

export default function Wallet() {
  const navigate = useNavigate();
  const [wallet,          setWallet]          = useState({ balance: 0, walletBalance: 0, bankAccount: '', stylistName: '' });
  const [userDocBalance,  setUserDocBalance]  = useState(0); // from users/{uid}.walletBalance — used for marketplace
  const [history,         setHistory]         = useState([]);
  const [marketplaceOrders, setMarketplaceOrders] = useState([]);
  const [bankAccount,     setBankAccount]     = useState('');
  const [fundAmount,      setFundAmount]      = useState('');
  const [loading,         setLoading]         = useState(false);
  const [savingBank,      setSavingBank]      = useState(false);
  const [error,           setError]           = useState(null);

  const auth    = getAuth();
  const user    = auth.currentUser;
  const sub     = useSubscription();

  const isStylist      = !!wallet.stylistName;
  // Grooming wallet balance (from wallets/{uid})
  const groomBalance   = wallet.walletBalance || wallet.balance || 0;
  // Marketplace wallet balance (from users/{uid}) — same source as Merchant_checkout
  const marketBalance  = userDocBalance;

  // ── Firestore listeners ──────────────────────────────────────────────────
  useEffect(() => {
    if (!user) return;

    // 1. Grooming wallet doc
    const unsubWallet = onSnapshot(doc(db, 'wallets', user.uid), (snap) => {
      if (snap.exists()) {
        const data = snap.data();
        setWallet(prev => ({ ...prev, ...data }));
        if (data.bankAccount) setBankAccount(data.bankAccount);
      }
    });

    // 2. Barber doc (for stylist info)
    const unsubBarber = onSnapshot(doc(db, 'barbers', user.uid), (snap) => {
      if (snap.exists()) setWallet(prev => ({ ...prev, ...snap.data() }));
    });

    // 3. users/{uid} — authoritative marketplace wallet balance
    const unsubUser = onSnapshot(doc(db, 'users', user.uid), (snap) => {
      if (snap.exists()) {
        const data = snap.data();
        setUserDocBalance(data.walletBalance || 0);
      }
    });

    // 4. Grooming transactions (two-field approach as in original)
    const qCreatedAt = query(collection(db, 'transactions'), orderBy('createdAt', 'desc'), limit(20));
    const qTimestamp = query(collection(db, 'transactions'), orderBy('timestamp',  'desc'), limit(20));

    const mergeAndFilter = (snap1Docs, snap2Docs) => {
      const seen = new Set();
      const all  = [...snap1Docs, ...snap2Docs]
        .map(d => ({ id: d.id, ...d.data() }))
        .filter(tx => {
          if (tx.userId !== user.uid && tx.stylistId !== user.uid) return false;
          if (seen.has(tx.id)) return false;
          seen.add(tx.id);
          return true;
        })
        .sort((a, b) => {
          const ta = a.createdAt?.toDate?.() || a.timestamp?.toDate?.() || new Date(0);
          const tb = b.createdAt?.toDate?.() || b.timestamp?.toDate?.() || new Date(0);
          return tb - ta;
        })
        .slice(0, 15);
      setHistory(all);
    };

    let snap1Docs = [], snap2Docs = [];
    const unsubCA = onSnapshot(qCreatedAt, (snap) => {
      snap1Docs = snap.docs;
      mergeAndFilter(snap1Docs, snap2Docs);
    });
    const unsubTS = onSnapshot(qTimestamp, (snap) => {
      snap2Docs = snap.docs;
      mergeAndFilter(snap1Docs, snap2Docs);
    }, () => {}); // timestamp index may not exist — silently ignore

    // 5. Recent marketplace orders paid with this user's wallet
    const unsubMktOrders = onSnapshot(
      query(
        collection(db, 'merchant_orders'),
        where('buyerUid',       '==', user.uid),
        where('paymentMethod',  '==', 'wallet'),
        orderBy('createdAt',    'desc'),
        limit(10),
      ),
      snap => setMarketplaceOrders(snap.docs.map(d => ({ id: d.id, ...d.data() }))),
      () => {}, // index may not exist yet
    );

    return () => {
      unsubWallet(); unsubBarber(); unsubUser();
      unsubCA(); unsubTS(); unsubMktOrders();
    };
  }, [user]);

  // ── Monnify wallet top-up (preserved exactly from original) ──────────────
  const handleFundWallet = (e) => {
    e.preventDefault();
    const amount = parseFloat(fundAmount);
    if (!amount || amount < 500) return setError('Minimum top-up is ₦500');

    if (!window.MonnifySDK) return setError('Payment gateway not loaded. Please refresh the page.');

    const apiKey       = process.env.REACT_APP_MONNIFY_API_KEY;
    const contractCode = process.env.REACT_APP_MONNIFY_CONTRACT_CODE;
    if (!apiKey || !contractCode || contractCode === 'undefined' || contractCode === '') {
      return setError('Payment gateway not configured. Please contact support.');
    }

    setError(null);
    setLoading(true);

    window.MonnifySDK.initialize({
      amount,
      currency:           'NGN',
      reference:          `FUND_${user.uid.slice(0, 6)}_${Date.now()}`,
      customerFullName:   user.displayName || 'Esdras User',
      customerEmail:      user.email,
      apiKey,
      contractCode,
      paymentDescription: 'Esdras Wallet Top-up',
      metadata: {
        userId:      user.uid,
        paymentType: 'TOPUP',
      },
      onComplete: (res) => {
        setLoading(false);
        if (res.status === 'SUCCESS' || res.paymentStatus === 'PAID') {
          setFundAmount('');
          toast.success('Transaction received! Balance will reflect once verified.');
        } else {
          setError('Payment was not completed. Please try again.');
        }
      },
      onClose: () => setLoading(false),
    });
  };

  // ── Save bank details (preserved exactly from original) ──────────────────
  const saveBankDetails = async () => {
    if (!/^\d{10}$/.test(bankAccount)) {
      return setError('Please enter a valid 10-digit NUBAN account number.');
    }
    setSavingBank(true); setError(null);
    try {
      const trimmed = bankAccount.trim();
      await setDoc(doc(db, 'wallets', user.uid), { bankAccount: trimmed }, { merge: true });
      await setDoc(doc(db, 'barbers', user.uid), { bankAccount: trimmed }, { merge: true });
      toast.success('Bank details saved and synchronised!');
    } catch (err) {
      setError('Failed to save bank info. Please try again.');
    } finally {
      setSavingBank(false);
    }
  };

  // ── Transaction label helper (preserved from original) ───────────────────
  const getTxLabel = (item) => {
    const raw = (item.type || 'Transaction').replace(/_/g, ' ');
    const map = {
      'topup':                 'Wallet Top-up',
      'deposit':               'Wallet Deposit',
      'subscription monthly':  'Shield Monthly Plan',
      'subscription yearly':   'Shield Yearly Plan',
      'subscription annual':   'Shield Annual Plan',
      'style boost':           'Style Boost',
      'booking payment':       'Booking Payment',
    };
    return map[raw.toLowerCase()] || raw;
  };

  const isCredit = (item) =>
    item.type === 'TOPUP' || item.type === 'deposit' || item.stylistId === user?.uid;

  const getTxDate = (item) => {
    const ts = item.createdAt || item.timestamp;
    return ts?.toDate ? ts.toDate().toLocaleString('en-NG') : 'Recent';
  };

  if (!user) return <div style={loginStyle}>Please log in to access your wallet</div>;

  return (
    <div style={pageStyle}>
      <h1 style={titleStyle}>Esdras Wallet</h1>

      {/* ── Main balance card ── */}
      <div style={balanceCard}>
        <p style={{ fontSize: '3.5rem', margin: '0 0 4px', color: GOLD, fontWeight: '800' }}>
          {formatNaira(groomBalance)}
        </p>
        <p style={{ opacity: 0.6, letterSpacing: '2px', margin: 0 }}>AVAILABLE BALANCE</p>
      </div>

      {/* ── Subscription status strip ── */}
      {!sub.loading && (
        <div style={subStrip}>
          {sub.isSuperAdmin ? (
            <span style={adminPill}>⚡ SUPER ADMIN · UNLIMITED ACCESS</span>
          ) : sub.isSubscriptionActive ? (
            <span style={activePill}>
              👑 {sub.tier?.toUpperCase()} SHIELD ACTIVE
              &nbsp;·&nbsp;
              {sub.monthlyScansUsed}/{sub.PRICING?.[sub.tier]?.scansPerMonth || '—'} scans used this month
            </span>
          ) : (
            <span style={freePill}>
              FREE TIER · {sub.lifetimeScansUsed || 0}/4 scans used
              &nbsp;·&nbsp;
              <a href="/subscription" style={{ color: GOLD, fontWeight: '700' }}>UPGRADE →</a>
            </span>
          )}
        </div>
      )}

      {error && <div style={errorBox}>{error}</div>}

      <div style={mainGrid}>
        <div style={column}>
          {/* Fund wallet */}
          <div style={sectionCard}>
            <h3 style={{ color: GOLD, marginBottom: '1rem' }}>Fund Wallet</h3>
            <input
              type="number"
              placeholder="Amount (₦)"
              value={fundAmount}
              onChange={e => setFundAmount(e.target.value)}
              style={inputStyle}
            />
            <button onClick={handleFundWallet} disabled={loading} style={fundBtn}>
              {loading ? 'INITIALIZING...' : 'DEPOSIT VIA MONNIFY'}
            </button>
          </div>

          {/* ── MARKETPLACE WALLET SECTION ── */}
          <div style={{ ...sectionCard, border: `1px solid ${GOLD}33`, background: 'rgba(184,134,11,0.03)' }}>
            <h3 style={{ color: GOLD, marginBottom: '4px' }}>🛍️ Marketplace Wallet</h3>
            <p style={{ fontSize: '0.72rem', opacity: 0.5, marginBottom: '16px' }}>
              Your Esdras wallet balance can be used to purchase products on the Esdras Marketplace.
            </p>

            {/* Marketplace balance card */}
            <div style={mktBalCard}>
              <div>
                <p style={{ opacity: 0.5, fontSize: '0.6rem', letterSpacing: '2px', margin: '0 0 4px' }}>AVAILABLE FOR MARKETPLACE</p>
                <p style={{ color: GOLD, fontSize: '2rem', fontWeight: '900', margin: 0 }}>
                  {formatNaira(marketBalance)}
                </p>
                <p style={{ opacity: 0.4, fontSize: '0.65rem', margin: '4px 0 0' }}>
                  From your Esdras account · auto-deducted at checkout
                </p>
              </div>
              <button
                onClick={() => navigate('/')}
                style={{ background: GOLD, color: 'black', border: 'none', padding: '12px 20px', borderRadius: '50px', fontWeight: '800', cursor: 'pointer', fontSize: '0.78rem', whiteSpace: 'nowrap', alignSelf: 'center' }}>
                SHOP MARKETPLACE →
              </button>
            </div>

            {/* How it works */}
            <div style={{ marginTop: '14px', background: 'rgba(0,0,0,0.2)', borderRadius: '10px', padding: '12px 14px' }}>
              <p style={{ fontSize: '0.68rem', opacity: 0.5, margin: '0 0 8px', fontWeight: '700', letterSpacing: '1px' }}>HOW IT WORKS</p>
              {[
                '🛒  Browse the Marketplace on the Esdras homepage',
                '👛  At checkout, select "Pay with Esdras Wallet"',
                '✅  Balance is instantly deducted · No extra fees',
                '💰  Top up above to increase your marketplace budget',
              ].map(step => (
                <p key={step} style={{ fontSize: '0.72rem', opacity: 0.6, margin: '5px 0', lineHeight: '1.5' }}>{step}</p>
              ))}
            </div>

            {/* Recent marketplace wallet purchases */}
            {marketplaceOrders.length > 0 && (
              <div style={{ marginTop: '16px' }}>
                <p style={{ fontSize: '0.65rem', opacity: 0.4, letterSpacing: '1.5px', margin: '0 0 10px', fontWeight: '700' }}>
                  RECENT MARKETPLACE PURCHASES (WALLET)
                </p>
                {marketplaceOrders.map(order => (
                  <div key={order.id} style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', padding: '10px 0', borderBottom: '1px solid rgba(255,255,255,0.05)' }}>
                    <div>
                      <p style={{ fontWeight: '600', fontSize: '0.82rem', margin: '0 0 2px' }}>
                        {order.productName}
                      </p>
                      <p style={{ fontSize: '0.65rem', opacity: 0.4, margin: 0 }}>
                        {order.createdAt?.toDate?.().toLocaleDateString('en-NG') || ''}
                        {' · '}{order.merchantName}
                        {' · Qty: '}{order.qty}
                      </p>
                    </div>
                    <span style={{ color: '#ff4d4d', fontWeight: '700', fontSize: '0.9rem' }}>
                      −{formatNaira(order.totalPaid)}
                    </span>
                  </div>
                ))}
              </div>
            )}
          </div>

          {/* Bank settings (stylist only) */}
          {isStylist && (
            <div style={sectionCard}>
              <h3 style={{ color: GOLD, marginBottom: '1rem' }}>Bank Settings</h3>
              <p style={{ fontSize: '0.7rem', opacity: 0.5, marginBottom: '10px' }}>
                Enter your 10-digit NUBAN account number for withdrawals.
              </p>
              <input
                type="text"
                inputMode="numeric"
                placeholder="Bank Account Number"
                value={bankAccount}
                onChange={e => setBankAccount(e.target.value.replace(/\D/g, '').slice(0, 10))}
                style={inputStyle}
              />
              <button onClick={saveBankDetails} disabled={savingBank} style={saveBtn}>
                {savingBank ? 'Saving...' : 'Update Bank Info'}
              </button>
            </div>
          )}

          {/* Payout request (stylist only) */}
          {wallet.stylistName && (
            <PayoutRequest walletBalance={groomBalance} stylistName={wallet.stylistName} />
          )}
        </div>

        {/* Transaction history */}
        <div style={column}>
          <div style={sectionCard}>
            <h3 style={{ color: GOLD, marginBottom: '1rem' }}>Activity History</h3>
            {history.length === 0 ? (
              <p style={{ opacity: 0.5, textAlign: 'center', padding: '20px' }}>
                No transactions recorded yet
              </p>
            ) : (
              history.map(item => (
                <div key={item.id} style={historyItem}>
                  <div style={{ textAlign: 'left' }}>
                    <div style={{ fontWeight: 'bold', textTransform: 'uppercase', fontSize: '0.8rem' }}>
                      {getTxLabel(item)}
                    </div>
                    <div style={{ fontSize: '0.7rem', opacity: 0.5 }}>
                      {getTxDate(item)}
                    </div>
                    {item.plan && (
                      <div style={{ fontSize: '0.6rem', color: GOLD, marginTop: '2px' }}>
                        {item.plan} · {item.scansPerMonth} scans/mo
                      </div>
                    )}
                  </div>
                  <span style={{ color: isCredit(item) ? '#4caf50' : '#ff4d4d', fontWeight: 'bold', fontSize: '1.1rem' }}>
                    {isCredit(item) ? '+' : '−'}{formatNaira(item.amount)}
                  </span>
                </div>
              ))
            )}
          </div>
        </div>
      </div>
    </div>
  );
}

// ── Styles ────────────────────────────────────────────────────────────────────
const pageStyle   = { minHeight: '100vh', background: NAVY, color: 'white', fontFamily: 'Montserrat, sans-serif', padding: '2rem 1rem', textAlign: 'center' };
const titleStyle  = { color: GOLD, fontSize: '2.5rem', fontWeight: '800', marginBottom: '1rem' };
const mainGrid    = { display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(350px, 1fr))', gap: '20px', maxWidth: '1000px', margin: '0 auto' };
const column      = { display: 'flex', flexDirection: 'column', gap: '20px' };
const balanceCard = { background: 'rgba(255,255,255,0.05)', padding: '2rem', borderRadius: '24px', border: `2px solid ${GOLD}`, maxWidth: '500px', margin: '0 auto 1.5rem' };
const sectionCard = { background: 'rgba(255,255,255,0.03)', padding: '1.5rem', borderRadius: '20px', border: '1px solid rgba(255,255,255,0.1)', textAlign: 'left' };
const inputStyle  = { width: '100%', padding: '1rem', borderRadius: '12px', background: 'rgba(0,0,0,0.3)', border: '1px solid #333', color: 'white', marginBottom: '1rem', boxSizing: 'border-box', fontFamily: 'inherit' };
const fundBtn     = { background: GOLD, color: 'black', padding: '1rem', border: 'none', borderRadius: '50px', fontWeight: 'bold', width: '100%', cursor: 'pointer', fontFamily: 'inherit' };
const saveBtn     = { background: 'rgba(255,255,255,0.1)', color: 'white', padding: '0.8rem', borderRadius: '10px', border: 'none', width: '100%', marginBottom: '10px', cursor: 'pointer', fontFamily: 'inherit' };
const historyItem = { display: 'flex', justifyContent: 'space-between', alignItems: 'center', padding: '12px 0', borderBottom: '1px solid rgba(255,255,255,0.05)' };
const errorBox    = { color: '#ff4d4d', background: 'rgba(255,77,77,0.1)', padding: '10px 20px', borderRadius: '10px', maxWidth: '500px', margin: '0 auto 20px', textAlign: 'center' };
const loginStyle  = { height: '100vh', display: 'grid', placeItems: 'center', background: NAVY, color: GOLD, fontSize: '1.5rem' };
const subStrip    = { maxWidth: '600px', margin: '0 auto 20px', padding: '10px 20px', borderRadius: '50px', background: 'rgba(255,255,255,0.04)', border: '1px solid rgba(255,255,255,0.08)', fontSize: '0.72rem' };
const activePill  = { color: GOLD, fontWeight: '700', letterSpacing: '0.5px' };
const freePill    = { color: 'rgba(255,255,255,0.6)', letterSpacing: '0.5px' };
const adminPill   = { color: '#00ff88', fontWeight: '700', letterSpacing: '0.5px' };
const mktBalCard  = { display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', background: 'rgba(0,0,0,0.3)', borderRadius: '14px', padding: '16px', border: `1px solid ${GOLD}22`, gap: '12px', flexWrap: 'wrap' };
